/**
 * Portable block content (F-064, F-072, and knowledge articles).
 *
 * One document shape, three surfaces. An email, a landing page and a knowledge
 * article are the same JSON with different blocks permitted - not three
 * editors with three formats, which is how a block copied from an email into a
 * landing page loses its link.
 *
 * "Portable" is a claim this file has to earn, so nothing here is
 * channel-specific. A block carries what it says and how it looks in terms
 * every surface can honour; an email that cannot do a two-column layout
 * renders it stacked rather than storing a different block.
 */

export const BLOCK_TYPES = [
  'text',
  'heading',
  'image',
  'button',
  'divider',
  'spacer',
  'columns',
  'quote',
  'video',
  /**
   * An unsubscribe footer. A block rather than a setting because a marketing
   * email that cannot show where its unsubscribe link is has a compliance
   * problem an editor should be able to see.
   */
  'unsubscribe',
  /**
   * The lines of the record a document is generated from (F-050).
   *
   * A block rather than a token because it is a table, not a value: a quote
   * document without its lines is a letter about a quote. Only the document
   * palette allows it, so an email cannot ask for a table of line items it has
   * no record to draw from.
   */
  'line-items',
] as const;

export type BlockType = (typeof BLOCK_TYPES)[number];

export type BlockAlign = 'start' | 'center' | 'end';

export interface Block {
  id: string;
  type: BlockType;
  /** The words. Personalisation tokens live in here as {{token}}. */
  text?: string;
  /** For image and video. */
  url?: string;
  /** Required on every image: an image with no alt text is invisible to a reader who cannot see it. */
  alt?: string;
  /** For button: where it goes. */
  href?: string;
  align?: BlockAlign;
  /** For columns: the blocks inside, left to right. One level only. */
  children?: Block[][];
  /**
   * Dynamic content (G-178): shown only to recipients this holds for. Absent
   * means everybody. Never allowed on the unsubscribe footer.
   */
  showWhen?: ShowWhen;
}

/**
 * Who sees a block: by a field of theirs, by an audience they are in, or by
 * what they have done - clicked an email lately.
 */
export type ShowWhen =
  | { kind: 'field'; field: 'company' | 'title' | 'city' | 'country' | 'language'; operator: 'eq' | 'neq' | 'set' | 'unset'; value?: string }
  | { kind: 'segment'; segmentId: string; member: boolean }
  | { kind: 'engaged'; days: number; engaged: boolean };

/** What one recipient brings to the question of which blocks they see. */
export interface RecipientContext {
  values: Record<string, string | null | undefined>;
  segments: ReadonlySet<string>;
  /** Clicked an email within the window the condition asks about. */
  engagedWithin: (days: number) => boolean;
}

/** Whether a block is shown to this recipient. */
export function blockVisible(block: Block, recipient: RecipientContext): boolean {
  const rule = block.showWhen;
  if (!rule) return true;
  if (rule.kind === 'segment') return recipient.segments.has(rule.segmentId) === rule.member;
  if (rule.kind === 'engaged') return recipient.engagedWithin(rule.days) === rule.engaged;
  const raw = recipient.values[rule.field];
  const value = raw === null || raw === undefined ? '' : String(raw).trim();
  switch (rule.operator) {
    case 'set':
      return value !== '';
    case 'unset':
      return value === '';
    case 'eq':
      return value.toLowerCase() === (rule.value ?? '').trim().toLowerCase();
    case 'neq':
      return value.toLowerCase() !== (rule.value ?? '').trim().toLowerCase();
  }
}

/** The segments a document's conditions ask about, so a sender can resolve each once. */
export function segmentsReferenced(document: BlockDocument): string[] {
  const ids = new Set<string>();
  const walk = (blocks: Block[]): void => {
    for (const block of blocks) {
      if (block.showWhen?.kind === 'segment') ids.add(block.showWhen.segmentId);
      for (const column of block.children ?? []) walk(column);
    }
  };
  walk(document.blocks);
  return [...ids];
}

export interface BlockDocument {
  blocks: Block[];
}

/**
 * Which blocks a surface permits, and what it calls itself.
 *
 * The palette, in the same sense FlowCanvas has palettes: the difference
 * between three callers is a list, not a component. An email has no video
 * block because most mail clients will not play one; a landing page has no
 * unsubscribe footer because it is not a message.
 */
export interface BlockPalette {
  key: 'email' | 'landing-page' | 'article' | 'document';
  label: string;
  allowed: readonly BlockType[];
  /** Whether this surface sends, and therefore needs a subject and an unsubscribe. */
  isMessage: boolean;
  /** Width the canvas previews at, in the token scale's terms. */
  canvasWidth: 'reading' | 'panel-wide';
}

export const BLOCK_PALETTES: Record<BlockPalette['key'], BlockPalette> = {
  email: {
    key: 'email',
    label: 'Email',
    // No video: most mail clients refuse to play one, and a block that renders
    // as a broken box in Outlook is worse than one the palette never offered.
    allowed: ['text', 'heading', 'image', 'button', 'divider', 'spacer', 'columns', 'quote', 'unsubscribe'],
    isMessage: true,
    canvasWidth: 'reading',
  },
  'landing-page': {
    key: 'landing-page',
    label: 'Landing page',
    // No unsubscribe: a page is not a message, and a footer offering to stop
    // sending something would be meaningless.
    allowed: ['text', 'heading', 'image', 'button', 'divider', 'spacer', 'columns', 'quote', 'video'],
    isMessage: false,
    canvasWidth: 'panel-wide',
  },
  article: {
    key: 'article',
    label: 'Article',
    // No button and no spacer: a knowledge article is prose, and a call to
    // action inside one is a support article trying to sell something.
    allowed: ['text', 'heading', 'image', 'divider', 'quote', 'video'],
    isMessage: false,
    canvasWidth: 'reading',
  },
  document: {
    key: 'document',
    label: 'Document',
    /*
     * A generated document is printed or signed, not clicked (F-050).
     *
     * No button and no video: neither does anything on paper. No image yet: an
     * image block names a URL, and fetching arbitrary URLs from the server
     * while rendering a customer's quote is a request forgery waiting for a
     * template author to find it. No unsubscribe: a quote is not a campaign.
     */
    allowed: ['text', 'heading', 'divider', 'spacer', 'columns', 'quote', 'line-items'],
    isMessage: false,
    canvasWidth: 'reading',
  },
};

export interface BlockProblem {
  /** Which block, or '$' for the document. */
  path: string;
  message: string;
}

/**
 * What is wrong with a document, for this surface.
 *
 * Run by the editor as somebody types AND by the server before anything is
 * published, from this one function - so a document that looks publishable in
 * the editor is publishable, and one that is refused was refused for a reason
 * the editor already showed.
 */
export function validateBlocks(
  document: BlockDocument,
  palette: BlockPalette,
): BlockProblem[] {
  const problems: BlockProblem[] = [];
  const allowed = new Set<string>(palette.allowed);
  const seen = new Set<string>();

  const walk = (blocks: Block[], path: string): void => {
    for (const [index, block] of blocks.entries()) {
      const at = `${path}[${String(index)}]`;
      if (seen.has(block.id)) {
        // Duplicate ids make "edit this block" ambiguous, and the editor edits
        // by id.
        problems.push({ path: at, message: `Two blocks share the id ${block.id}.` });
      }
      seen.add(block.id);

      if (!allowed.has(block.type)) {
        problems.push({
          path: at,
          message: `A ${palette.label.toLowerCase()} cannot contain a ${block.type} block.`,
        });
      }
      if (block.type === 'image') {
        if (!block.url?.trim()) problems.push({ path: at, message: 'This image has no file.' });
        if (!block.alt?.trim()) {
          // Not a warning. An image with no alt text is invisible to a reader
          // using a screen reader and to anybody whose mail client blocks
          // images, which is most of them by default.
          problems.push({ path: at, message: 'Every image needs a description for people who cannot see it.' });
        }
      }
      if (block.showWhen) {
        if (block.type === 'unsubscribe') {
          // Everybody is shown how to stop receiving these. A footer only some
          // recipients see is a compliance problem, however it was meant.
          problems.push({ path: at, message: 'The unsubscribe footer is shown to everybody; it cannot depend on who they are.' });
        } else if (block.showWhen.kind === 'field' && (block.showWhen.operator === 'eq' || block.showWhen.operator === 'neq') && !block.showWhen.value?.trim()) {
          problems.push({ path: at, message: 'Say what the field has to be for this block to show.' });
        } else if (block.showWhen.kind === 'engaged' && !(block.showWhen.days >= 1 && block.showWhen.days <= 365)) {
          problems.push({ path: at, message: 'Engagement is counted over 1 to 365 days.' });
        }
      }
      if (block.type === 'button') {
        if (!block.text?.trim()) problems.push({ path: at, message: 'This button has no words on it.' });
        if (!block.href?.trim()) {
          problems.push({ path: at, message: 'This button goes nowhere.' });
        }
      }
      if (block.type === 'columns') {
        const columns = block.children ?? [];
        if (columns.length < 2) {
          problems.push({ path: at, message: 'A columns block needs at least two columns.' });
        }
        for (const [column, inner] of columns.entries()) {
          if (inner.some((child) => child.type === 'columns')) {
            problems.push({
              path: `${at}.children[${String(column)}]`,
              message: 'Columns cannot be nested. Mail clients do not render them reliably.',
            });
          }
          walk(inner, `${at}.children[${String(column)}]`);
        }
      }
    }
  };
  walk(document.blocks, '$');

  if (palette.isMessage && !document.blocks.some((block) => block.type === 'unsubscribe')) {
    problems.push({
      path: '$',
      message: 'A marketing email needs an unsubscribe footer.',
    });
  }
  if (document.blocks.length === 0) {
    problems.push({ path: '$', message: 'There is nothing in this yet.' });
  }

  return problems;
}

// ---------------------------------------------------------------------------
// Personalisation
// ---------------------------------------------------------------------------

const TOKEN = /\{\{\s*([a-zA-Z0-9_.]+)\s*(?:\|\s*([^}]*?)\s*)?\}\}/g;

export interface TokenUse {
  token: string;
  /** The text after `|`, used when the field is empty. */
  fallback: string | null;
}

/** Every personalisation token in a document, with whatever fallback it declares. */
export function tokensIn(document: BlockDocument): TokenUse[] {
  const found: TokenUse[] = [];
  const scan = (text: string | undefined): void => {
    if (!text) return;
    for (const match of text.matchAll(TOKEN)) {
      found.push({ token: match[1] ?? '', fallback: match[2] ?? null });
    }
  };
  const walk = (blocks: Block[]): void => {
    for (const block of blocks) {
      scan(block.text);
      scan(block.href);
      for (const column of block.children ?? []) walk(column);
    }
  };
  walk(document.blocks);
  return found;
}

/**
 * Fills tokens in, using the fallback where a value is missing.
 *
 * The fallback is the whole reason the syntax has one. `Hi {{firstName}},`
 * against a contact with no first name produces "Hi ," - which is the single
 * most recognisable way to tell a recipient that they are a row in a
 * spreadsheet. `{{firstName|there}}` produces "Hi there,".
 *
 * A token with no fallback and no value renders as the token's own name in
 * braces rather than as nothing, because an editor previewing their own email
 * has to be able to SEE which token had no value. Silently emptying it is what
 * produces "Hi ," in production after a clean-looking preview.
 */
export function renderText(
  text: string,
  values: Record<string, string | null | undefined>,
): string {
  return text.replace(TOKEN, (_whole, token: string, fallback: string | undefined) => {
    const value = values[token];
    if (value !== null && value !== undefined && value !== '') return value;
    if (fallback !== undefined && fallback !== '') return fallback;
    return `{{${token}}}`;
  });
}

/**
 * Tokens in this document that would render as nothing for a given sample.
 *
 * What the editor shows beside the preview. A token with a fallback is never
 * listed, because it cannot produce an empty greeting.
 */
export function tokensWithoutFallback(
  document: BlockDocument,
  sample: Record<string, string | null | undefined>,
): string[] {
  const missing = new Set<string>();
  for (const use of tokensIn(document)) {
    const value = sample[use.token];
    const hasValue = value !== null && value !== undefined && value !== '';
    if (!hasValue && (use.fallback === null || use.fallback === '')) missing.add(use.token);
  }
  return [...missing].sort();
}
